import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Bot, Download, FileDown, RotateCcw } from 'lucide-react';
import { Button, Dialog } from '@/design-system';
import { navigate } from '@/app/router';
import { localizeDocumentationWarning, localizeSystemValue } from '@/app/localization';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import { useAuthStore } from '@/features/auth/state/auth-store';
import { useEditorStore } from '@/features/funnel-editor/state/editor-store';
import { normalizeEditorGraph } from '@/features/funnel-editor/domain/normalize-graph';
import { useAutosave } from '@/features/funnel-editor/persistence/use-autosave';
import { FUNNEL_SCHEMA_VERSION } from '@/domain/funnel/schema-version';
import { useSimulation } from '@/features/funnel-editor/simulation/use-simulation';
import { useDiagnosis } from '@/features/funnel-editor/diagnosis/use-diagnosis';
import { buildDocumentationContext, readDocumentationExtension } from './graph-bridge';
import {
  generateFunnelDocumentation,
  objectiveSourceFingerprint,
  overviewSourceFingerprint,
  sopSourceFingerprint,
} from '@/domain/documentation/generator';
import type {
  DocumentationGraphExtension,
  DocumentationKpi,
  DocumentationSource,
  GeneratedDocumentation,
  ChecklistItem,
  SopStep,
} from '@/domain/documentation/contracts';
import { sourceFingerprint } from '@/domain/documentation/fingerprint';
import { buildDocumentationMarkdown } from '@/domain/documentation/markdown';
import { sanitizeExportFilename } from '@/domain/export-import/filename';
import { downloadBlob } from '@/features/export/download';
import { exportPdf } from '@/features/export/export-visual';
import { apiPost } from '@/infrastructure/api-client';
import type { AIInsightResponse } from '@/domain/ai/contracts';
import { DocumentationExportSurface } from './DocumentationExportSurface';
import { getNodeDefinition, STATUS_LABELS } from '@/features/funnel-editor/nodes/registry/node-registry';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; name: string; description?: string | null; revision: number };

function SourceBadge({ source }: { source: DocumentationSource }) {
  const label =
    source === 'ai_assisted' ? 'Assistida por IA' : source === 'manual' ? 'Manual' : 'Derivada';
  return (
    <span className="rounded border border-border-subtle px-2 py-0.5 text-[10px] uppercase tracking-wide text-text-muted">
      Origem: {label}
    </span>
  );
}

function Section({
  id,
  title,
  source,
  stale,
  actions,
  children,
}: {
  id: string;
  title: string;
  source: DocumentationSource;
  stale?: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} tabIndex={-1} className="scroll-mt-6 border-b border-border-subtle py-8">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold text-text-primary">{title}</h2>
        <SourceBadge source={source} />
        {stale ? (
          <span role="status" className="text-xs text-[#e3ad50]">
            A origem mudou desde que esta substituição foi criada
          </span>
        ) : null}
        <div className="ml-auto flex gap-2">{actions}</div>
      </div>
      {children}
    </section>
  );
}

function metricValue(value: unknown, unit: string) {
  if (typeof value !== 'number') return 'Desconhecido';
  if (unit === 'ratio') return `${(value * 100).toFixed(1)}%`;
  if (unit === 'money_minor')
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      value / 100,
    );
  return new Intl.NumberFormat('pt-BR').format(value);
}

export function DocumentationPage({ id }: { id: string }) {
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const initialize = useEditorStore((state) => state.initialize);
  useEffect(() => {
    let active = true;
    void dashboardApi
      .getFunnel(id)
      .then(({ funnel }) => {
        if (!active) return;
        initialize(normalizeEditorGraph(funnel.graph));
        setLoad({
          status: 'ready',
          name: funnel.name,
          description: funnel.description,
          revision: funnel.revision,
        });
      })
      .catch((error) => {
        if (active)
          setLoad({
            status: 'error',
            message: error instanceof Error ? error.message : 'Não foi possível abrir a documentação.',
          });
      });
    return () => {
      active = false;
    };
  }, [id, initialize]);
  if (load.status === 'loading')
    return (
      <main
        role="status"
        className="flex min-h-[60vh] items-center justify-center text-sm text-text-muted"
      >
        Gerando documentação…
      </main>
    );
  if (load.status === 'error')
    return (
      <main role="alert" className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
        <h1 className="text-xl font-semibold">Não foi possível abrir a documentação</h1>
        <p>{load.message}</p>
        <Button variant="secondary" onClick={() => navigate('/app')}>
          Voltar ao painel
        </Button>
      </main>
    );
  return (
    <DocumentationWorkspace
      funnelId={id}
      name={load.name}
      description={load.description}
      revision={load.revision}
    />
  );
}

function DocumentationWorkspace({
  funnelId,
  name,
  description,
  revision,
}: {
  funnelId: string;
  name: string;
  description?: string | null;
  revision: number;
}) {
  const graph = useEditorStore((state) => state.graph)!;
  const setDocumentation = useEditorStore((state) => state.setDocumentation);
  const user = useAuthStore((state) => state.user);
  const simulation = useSimulation();
  const diagnosis = useDiagnosis();
  const autosave = useAutosave({
    funnelId,
    userId: user?.id ?? '',
    initialRevision: revision,
    schemaVersion: FUNNEL_SCHEMA_VERSION,
  });
  const [editingOverview, setEditingOverview] = useState(false);
  const [overviewDraft, setOverviewDraft] = useState('');
  const [editingObjective, setEditingObjective] = useState(false);
  const [objectiveDraft, setObjectiveDraft] = useState({
    objective: '',
    metric: '',
    target: '',
    period: '',
  });
  const [editingStep, setEditingStep] = useState<SopStep | null>(null);
  const [stepDraft, setStepDraft] = useState({ title: '', description: '', outcome: '' });
  const [editingKpi, setEditingKpi] = useState<DocumentationKpi | null>(null);
  const [kpiDraft, setKpiDraft] = useState({
    label: '',
    owner: '',
    note: '',
    target: '',
    period: '',
  });
  const [editingChecklist, setEditingChecklist] = useState<ChecklistItem | null>(null);
  const [checklistDraft, setChecklistDraft] = useState({ label: '', required: true });
  const [exportSettingsOpen, setExportSettingsOpen] = useState(false);
  const [exportDraft, setExportDraft] = useState({
    clientName: '',
    branding: 'dark_funnels' as 'dark_funnels' | 'client' | 'minimal',
  });
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(() => new Set());
  const [exportError, setExportError] = useState<string | null>(null);
  const [pendingExport, setPendingExport] = useState<'md' | 'pdf' | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPreview, setAiPreview] = useState<string | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  const context = useMemo(
    () =>
      buildDocumentationContext(
        graph,
        { name, description, ownerName: user?.displayName },
        {
          ...(simulation?.activeScenario?.target
            ? {
                scenarioTarget: {
                  metric: simulation.activeScenario.target.metric,
                  value: simulation.activeScenario.target.value,
                },
              }
            : {}),
          ...(diagnosis
            ? {
                diagnosis: diagnosis.result.items.map((item) => ({
                  title: item.title,
                  severity: item.severity,
                  evidence: item.evidence.map((entry) => entry.explanation),
                })),
                diagnosisCoverage: diagnosis.result.coverage.overall,
              }
            : {}),
        },
      ),
    [graph, name, description, user?.displayName, simulation, diagnosis],
  );
  const document = useMemo(() => generateFunnelDocumentation(context), [context]);
  const config = readDocumentationExtension(graph);

  function updateConfig(
    update: (current: DocumentationGraphExtension) => DocumentationGraphExtension,
  ) {
    setDocumentation(update(config));
  }
  function saveOverview(content: string, source: DocumentationSource = 'manual') {
    updateConfig((current) => ({
      ...current,
      overviewOverride: {
        content: content.trim(),
        source,
        sourceFingerprint: overviewSourceFingerprint(context),
      },
    }));
    setEditingOverview(false);
  }
  function resetOverview() {
    updateConfig((current) => {
      const next = { ...current };
      delete next.overviewOverride;
      return next;
    });
  }
  function saveObjective() {
    if (!objectiveDraft.objective.trim()) return;
    updateConfig((current) => ({
      ...current,
      objective: {
        objective: objectiveDraft.objective.trim(),
        ...(objectiveDraft.metric.trim() ? { metric: objectiveDraft.metric.trim() } : {}),
        ...(objectiveDraft.target.trim() && Number.isFinite(Number(objectiveDraft.target))
          ? { target: Number(objectiveDraft.target) }
          : {}),
        ...(objectiveDraft.period.trim() ? { period: objectiveDraft.period.trim() } : {}),
        source: 'manual',
        sourceFingerprint: objectiveSourceFingerprint(context),
      },
    }));
    setEditingObjective(false);
  }
  function resetObjective() {
    updateConfig((current) => {
      const next = { ...current };
      delete next.objective;
      return next;
    });
  }
  function saveStep() {
    if (!editingStep) return;
    const existing = config.sop?.steps ?? [];
    const base = context.nodes.find((node) => node.id === editingStep.nodeRefs[0]);
    const overlay = {
      nodeId: editingStep.nodeRefs[0]!,
      title: stepDraft.title.trim(),
      description: stepDraft.description.trim(),
      ...(stepDraft.outcome.trim() ? { expectedOutcome: stepDraft.outcome.trim() } : {}),
      order: editingStep.order - 1,
      source: 'manual' as const,
      sourceFingerprint: sourceFingerprint(
        base
          ? {
              id: base.id,
              name: base.name,
              description: base.description,
              responsible: base.responsible,
              systems: base.systemName ? [base.systemName] : undefined,
            }
          : editingStep.nodeRefs[0],
      ),
    };
    updateConfig((current) => ({
      ...current,
      sop: {
        ...(current.sop ?? {}),
        source: 'manual',
        sourceFingerprint: sopSourceFingerprint(context),
        steps: [...existing.filter((item) => item.nodeId !== overlay.nodeId), overlay],
      },
    }));
    setEditingStep(null);
  }
  function resetSop() {
    updateConfig((current) => {
      const next = { ...current };
      delete next.sop;
      return next;
    });
    setRegenerateOpen(false);
  }
  function moveStep(nodeId: string, delta: number) {
    const ordered = document.sop.content.steps.map((step) => step.nodeRefs[0]!);
    const index = ordered.indexOf(nodeId);
    const target = index + delta;
    if (target < 0 || target >= ordered.length) return;
    [ordered[index], ordered[target]] = [ordered[target]!, ordered[index]!];
    const previous = new Map((config.sop?.steps ?? []).map((item) => [item.nodeId, item]));
    updateConfig((current) => ({
      ...current,
      sop: {
        ...(current.sop ?? {}),
        source: current.sop?.source ?? 'manual',
        sourceFingerprint: sopSourceFingerprint(context),
        steps: ordered.map((id, order) => ({
          ...(previous.get(id) ?? {
            nodeId: id,
            source: 'manual' as const,
            sourceFingerprint: sourceFingerprint(context.nodes.find((node) => node.id === id)),
          }),
          order,
        })),
      },
    }));
  }
  function persistChecklist() {
    const items = document.checklist.content.map((item, index) => ({
      id: item.id,
      ...(item.nodeRefs[0] ? { stepNodeId: item.nodeRefs[0] } : {}),
      label: item.label,
      required: item.required,
      order: index + 1,
      custom: item.nodeRefs.length === 0,
    }));
    updateConfig((current) => ({ ...current, checklistItems: items }));
  }
  function checklistOverlay() {
    return (
      config.checklistItems ??
      document.checklist.content.map((item, index) => ({
        id: item.id,
        ...(item.nodeRefs[0] ? { stepNodeId: item.nodeRefs[0] } : {}),
        label: item.label,
        required: item.required,
        order: index + 1,
        custom: item.nodeRefs.length === 0,
      }))
    );
  }
  function saveChecklistItem() {
    if (!editingChecklist || !checklistDraft.label.trim()) return;
    updateConfig((current) => ({
      ...current,
      checklistItems: checklistOverlay().map((item) =>
        item.id === editingChecklist.id
          ? { ...item, label: checklistDraft.label.trim(), required: checklistDraft.required }
          : item,
      ),
    }));
    setEditingChecklist(null);
  }
  function moveChecklistItem(id: string, delta: number) {
    const items = checklistOverlay().sort((left, right) => left.order - right.order);
    const index = items.findIndex((item) => item.id === id);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= items.length) return;
    [items[index], items[target]] = [items[target]!, items[index]!];
    updateConfig((current) => ({
      ...current,
      checklistItems: items.map((item, order) => ({ ...item, order: order + 1 })),
    }));
  }
  function saveKpi() {
    if (!editingKpi || !kpiDraft.label.trim()) return;
    const existing = config.kpis ?? [];
    updateConfig((current) => ({
      ...current,
      kpis: [
        ...existing.filter((item) => item.id !== editingKpi.id),
        {
          id: editingKpi.id,
          label: kpiDraft.label.trim(),
          ...(kpiDraft.owner.trim() ? { owner: kpiDraft.owner.trim() } : {}),
          ...(kpiDraft.note.trim() ? { note: kpiDraft.note.trim() } : {}),
          ...(kpiDraft.target.trim() && Number.isFinite(Number(kpiDraft.target))
            ? { target: Number(kpiDraft.target) }
            : {}),
          ...(kpiDraft.period.trim() ? { targetPeriod: kpiDraft.period.trim() } : {}),
        },
      ],
    }));
    setEditingKpi(null);
  }
  function excludeKpi(kpi: DocumentationKpi) {
    updateConfig((current) => ({
      ...current,
      kpis: [
        ...(current.kpis ?? []).filter((item) => item.id !== kpi.id),
        { id: kpi.id, included: false },
      ],
    }));
  }
  function addChecklistItem() {
    const label = window.prompt('Item da checklist');
    if (!label?.trim()) return;
    updateConfig((current) => ({
      ...current,
      checklistItems: [
        ...(current.checklistItems ??
          document.checklist.content.map((item, index) => ({
            id: item.id,
            stepNodeId: item.nodeRefs[0],
            label: item.label,
            required: item.required,
            order: index + 1,
            custom: false,
          }))),
        {
          id: `custom-${crypto.randomUUID()}`,
          label: label.trim(),
          required: false,
          order: document.checklist.content.length + 1,
          custom: true,
        },
      ],
    }));
  }

  async function runExport(format: 'md' | 'pdf') {
    setExportError(null);
    setPendingExport(format);
    if (!(await autosave.flush())) {
      setExportError('Não foi possível salvar a documentação mais recente. Tente novamente ou cancele a exportação.');
      return;
    }
    try {
      const filename = `${sanitizeExportFilename(name)}-documentation`;
      if (format === 'md')
        downloadBlob(
          new Blob([buildDocumentationMarkdown(name, document)], {
            type: 'text/markdown;charset=utf-8',
          }),
          `${filename}.md`,
        );
      else {
        const surface = exportRef.current;
        if (!surface) throw new Error('A superfície de PDF não está disponível.');
        const result = await exportPdf(surface);
        downloadBlob(result.blob, `${filename}.pdf`);
      }
      setPendingExport(null);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'A exportação falhou.');
    }
  }
  async function rewriteWithAI() {
    setAiLoading(true);
    setExportError(null);
    try {
      const response = await apiPost<{ result: AIInsightResponse }>(`/api/funnels/${funnelId}/ai`, {
        operation: 'rewrite',
        context: {
          instruction: 'Reescreva esta visão geral do funil com clareza, sem adicionar fatos.',
          overview: document.overview.content,
          funnelName: name,
        },
        sourceArtifacts: [{ artifactType: 'graph', artifactId: funnelId }],
      });
      setAiPreview(response.result.summary);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'A reescrita por IA está indisponível.');
    } finally {
      setAiLoading(false);
    }
  }
  function focusNode(nodeId: string) {
    navigate(`/app/funnels/${funnelId}?focusNode=${encodeURIComponent(nodeId)}`);
  }

  const toc = document.sections.map((item) => ({ id: item.id, title: item.title }));
  return (
    <main className="min-h-[calc(100vh-3.5rem)] bg-bg-app">
      <header className="sticky top-0 z-20 flex flex-wrap items-center gap-3 border-b border-border-subtle bg-surface/95 px-5 py-3 backdrop-blur">
        <button
          type="button"
          aria-label="Voltar ao editor"
          onClick={() => navigate(`/app/funnels/${funnelId}`)}
          className="rounded p-2 text-text-secondary hover:bg-surface-2"
        >
          <ArrowLeft size={17} />
        </button>
        <div>
          <h1 className="font-semibold">Documentação de {name}</h1>
          <p role="status" className="text-xs text-text-muted">
            {autosave.state === 'saving'
              ? 'Salvando…'
              : autosave.state === 'dirty'
                ? 'Alterações não salvas'
                : autosave.state === 'error'
                  ? 'Falha ao salvar'
                  : 'Salvo'}{' '}
            · Cobertura {Math.round(document.coverage.ratio * 100)}%
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" onClick={() => void runExport('md')}>
            <Download size={15} /> Markdown
          </Button>
          <Button variant="secondary" onClick={() => void runExport('pdf')}>
            <FileDown size={15} /> PDF
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setExportDraft({
                clientName: config.export.clientName ?? '',
                branding: config.export.branding,
              });
              setExportSettingsOpen(true);
            }}
          >
            Configurações de exportação
          </Button>
        </div>
      </header>
      {exportError ? (
        <div
          role="alert"
          className="flex items-center gap-3 border-b border-[rgba(226,85,85,0.4)] bg-[rgba(226,85,85,0.08)] px-6 py-2 text-sm text-[#f08484]"
        >
          <span>{exportError}</span>
          {pendingExport ? (
            <button className="underline" onClick={() => void runExport(pendingExport)}>
              Tentar novamente
            </button>
          ) : null}
          <button
            className="underline"
            onClick={() => {
              setPendingExport(null);
              setExportError(null);
            }}
          >
            Cancelar
          </button>
        </div>
      ) : null}
      <div className="mx-auto grid max-w-[1280px] grid-cols-[220px_minmax(0,1fr)] gap-10 px-6">
        <nav aria-label="Seções da documentação" className="sticky top-24 h-fit py-8">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">
            Conteúdo
          </p>
          {toc.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className="block rounded px-2 py-1.5 text-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary"
            >
              {item.title}
            </a>
          ))}
          <div className="mt-6 rounded border border-border-subtle p-3 text-xs text-text-muted">
            <p>Responsável pelo documento</p>
            <p className="mt-1 font-medium text-text-secondary">
              {document.documentOwner ?? 'Não definido'}
            </p>
            <p className="mt-2">As responsabilidades por etapa permanecem separadas.</p>
          </div>
        </nav>
        <article className="min-w-0 pb-20">
          {document.warnings.length ? (
            <aside
              aria-label="Avisos da documentação"
              className="mt-6 rounded border border-[rgba(198,144,50,0.35)] bg-[rgba(198,144,50,0.06)] p-4"
            >
              <h2 className="text-sm font-semibold text-text-brand">Observações da documentação</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-text-secondary">
                {document.warnings.map((warning, index) => (
                  <li key={`${warning.code}-${index}`}>{localizeDocumentationWarning(warning)}</li>
                ))}
              </ul>
            </aside>
          ) : null}
          <Section
            id="overview"
            title="Visão geral"
            source={document.overview.source}
            stale={document.overview.stale}
            actions={
              <>
                {document.overview.source !== 'derived' ? (
                  <button className="text-xs underline" onClick={resetOverview}>
                    Redefinir para derivada
                  </button>
                ) : null}
                <button
                  className="text-xs underline"
                  onClick={() => {
                    setOverviewDraft(document.overview.content);
                    setEditingOverview(true);
                  }}
                >
                  Substituir
                </button>
                <button
                  disabled={aiLoading}
                  className="flex items-center gap-1 text-xs underline disabled:opacity-50"
                  onClick={() => void rewriteWithAI()}
                >
                  <Bot size={13} /> {aiLoading ? 'Reescrevendo…' : 'Reescrever com IA'}
                </button>
              </>
            }
          >
            {editingOverview ? (
              <div className="space-y-2">
                <textarea
                  aria-label="Substituição da visão geral"
                  rows={5}
                  className="w-full rounded border border-border-subtle bg-surface-2 p-3"
                  value={overviewDraft}
                  onChange={(event) => setOverviewDraft(event.target.value)}
                />
                <Button onClick={() => saveOverview(overviewDraft)}>Salvar substituição</Button>
                <Button variant="secondary" onClick={() => setEditingOverview(false)}>
                  Cancelar
                </Button>
              </div>
            ) : (
              <p className="whitespace-pre-wrap text-text-secondary">{document.overview.content}</p>
            )}
          </Section>
          <Section
            id="objective"
            title="Objetivo"
            source={document.objective.source}
            stale={document.objective.stale}
            actions={
              <>
                {document.objective.content?.source !== 'derived' ? (
                  <button className="text-xs underline" onClick={resetObjective}>
                    Redefinir para derivada
                  </button>
                ) : null}
                <button
                  className="text-xs underline"
                  onClick={() => {
                    const value = document.objective.content;
                    setObjectiveDraft({
                      objective: value?.objective ?? '',
                      metric: value?.metric ?? '',
                      target: value?.target === undefined ? '' : String(value.target),
                      period: value?.period ?? '',
                    });
                    setEditingObjective(true);
                  }}
                >
                  Substituir
                </button>
              </>
            }
          >
            {editingObjective ? (
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  aria-label="Objetivo"
                  className="rounded border border-border-subtle bg-surface-2 p-2 sm:col-span-2"
                  value={objectiveDraft.objective}
                  onChange={(event) =>
                    setObjectiveDraft({ ...objectiveDraft, objective: event.target.value })
                  }
                />
                <input
                  aria-label="Métrica do objetivo"
                  placeholder="Métrica"
                  className="rounded border border-border-subtle bg-surface-2 p-2"
                  value={objectiveDraft.metric}
                  onChange={(event) =>
                    setObjectiveDraft({ ...objectiveDraft, metric: event.target.value })
                  }
                />
                <input
                  aria-label="Meta do objetivo"
                  placeholder="Meta"
                  type="number"
                  className="rounded border border-border-subtle bg-surface-2 p-2"
                  value={objectiveDraft.target}
                  onChange={(event) =>
                    setObjectiveDraft({ ...objectiveDraft, target: event.target.value })
                  }
                />
                <input
                  aria-label="Período do objetivo"
                  placeholder="Período"
                  className="rounded border border-border-subtle bg-surface-2 p-2"
                  value={objectiveDraft.period}
                  onChange={(event) =>
                    setObjectiveDraft({ ...objectiveDraft, period: event.target.value })
                  }
                />
                <div>
                  <Button onClick={saveObjective}>Salvar objetivo</Button>
                </div>
              </div>
            ) : document.objective.content ? (
              <div>
                <p className="text-text-secondary">{document.objective.content.objective}</p>
                {document.objective.content.target !== undefined ? (
                  <p className="mt-2 text-sm">
                    {document.objective.content.metric}: {document.objective.content.target}{' '}
                    {document.objective.content.period
                      ? `· ${document.objective.content.period}`
                      : ''}
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-text-muted">Nenhum objetivo definido.</p>
            )}
          </Section>
          <Section id="stages" title="Etapas" source="derived">
            <div className="grid gap-3 sm:grid-cols-2">
              {document.stages.content.map((stage) => (
                <article
                  key={stage.id}
                  className="rounded border border-border-subtle bg-surface p-4"
                >
                  <button
                    className="text-left font-medium hover:text-text-brand"
                    onClick={() => focusNode(stage.id)}
                  >
                    {stage.name}
                  </button>
                  <p className="mt-1 text-sm text-text-secondary">
                    {stage.description ?? 'Descrição não informada.'}
                  </p>
                  <p className="mt-2 text-xs text-text-muted">
                    {getNodeDefinition(stage.type)?.label ?? stage.type} ·{' '}
                    {stage.status && stage.status in STATUS_LABELS
                      ? STATUS_LABELS[stage.status as keyof typeof STATUS_LABELS]
                      : 'Ativo'}
                  </p>
                </article>
              ))}
            </div>
          </Section>
          <Section id="responsibilities" title="Responsabilidades" source="derived">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  <th className="pb-2">Responsável</th>
                  <th className="pb-2">Etapas</th>
                </tr>
              </thead>
              <tbody>
                {document.responsibilities.content.map((item) => (
                  <tr key={item.responsible} className="border-t border-border-subtle">
                    <td className="py-3">{item.responsible}</td>
                    <td>
                      {item.stages.map((stage, index) => (
                        <button
                          key={item.nodeRefs[index]}
                          className="mr-2 underline"
                          onClick={() => focusNode(item.nodeRefs[index]!)}
                        >
                          {stage}
                        </button>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!document.responsibilities.content.length ? (
              <p className="text-text-muted">Nenhuma responsabilidade por etapa definida.</p>
            ) : null}
          </Section>
          <Section id="systems" title="Sistemas" source="derived">
            <div className="grid gap-3 sm:grid-cols-2">
              {document.systems.content.map((system) => (
                <article key={system.id} className="rounded border border-border-subtle p-3">
                  <h3 className="font-medium">{system.name}</h3>
                  <p className="text-sm text-text-secondary">{system.role}</p>
                  <button
                    className="mt-2 text-xs underline"
                    onClick={() => focusNode(system.nodeRefs[0]!)}
                  >
                    Abrir etapa
                  </button>
                </article>
              ))}
            </div>
            {!document.systems.content.length ? (
              <p className="text-text-muted">
                Não há sistemas explícitos disponíveis; fornecedores nunca são presumidos.
              </p>
            ) : null}
          </Section>
          <Section
            id="kpis"
            title="KPIs"
            source="derived"
            actions={
              config.kpis?.length ? (
                <button
                  className="text-xs underline"
                  onClick={() =>
                    updateConfig((current) => {
                      const next = { ...current };
                      delete next.kpis;
                      return next;
                    })
                  }
                >
                  Redefinir curadoria de KPI
                </button>
              ) : null
            }
          >
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  <th className="pb-2">KPI</th>
                  <th className="pb-2">Atual</th>
                  <th className="pb-2">Meta</th>
                  <th className="pb-2">Responsável</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {document.kpis.content.map((item) => (
                  <tr key={item.id} className="border-t border-border-subtle">
                    <td className="py-3">{item.label}</td>
                    <td>{metricValue(item.current.effectiveValue, item.unit)}</td>
                    <td>{item.target ?? '—'}</td>
                    <td>{item.owner ?? '—'}</td>
                    <td className="space-x-2">
                      {item.nodeRefs[0] ? (
                        <button className="underline" onClick={() => focusNode(item.nodeRefs[0]!)}>
                          Abrir origem
                        </button>
                      ) : null}
                      <button
                        className="underline"
                        onClick={() => {
                          setEditingKpi(item);
                          setKpiDraft({
                            label: item.label,
                            owner: item.owner ?? '',
                            note: item.note ?? '',
                            target: item.target === undefined ? '' : String(item.target),
                            period: item.period ?? '',
                          });
                        }}
                      >
                        Configurar
                      </button>
                      <button className="underline" onClick={() => excludeKpi(item)}>
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {document.kpis.content.map((item) =>
              item.note ? (
                <p key={`${item.id}-note`} className="mt-2 text-xs text-text-muted">
                  {item.label}: {item.note}
                </p>
              ) : null,
            )}
          </Section>
          <Section
            id="sop"
            title="POP"
            source={document.sop.source}
            stale={document.sop.stale}
            actions={
              <>
                <button
                  className="flex items-center gap-1 text-xs underline"
                  onClick={() => setRegenerateOpen(true)}
                >
                  <RotateCcw size={12} /> Gerar novamente
                </button>
                {document.sop.source !== 'derived' ? (
                  <button className="text-xs underline" onClick={resetSop}>
                    Redefinir para derivada
                  </button>
                ) : null}
              </>
            }
          >
            <p className="mb-5 text-sm text-text-secondary">{document.sop.content.purpose}</p>
            <ol className="space-y-3">
              {document.sop.content.steps.map((step, index) => (
                <li key={step.id} className="rounded border border-border-subtle bg-surface p-4">
                  <div className="flex gap-3">
                    <span className="text-text-brand">{step.order}.</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          className="font-medium hover:text-text-brand"
                          onClick={() => focusNode(step.nodeRefs[0]!)}
                        >
                          {step.title}
                        </button>
                        <SourceBadge source={step.source} />
                        {step.stale ? (
                          <span className="text-xs text-[#e3ad50]">Origem alterada</span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-sm text-text-secondary">{step.description}</p>
                      {step.branch ? (
                        <p className="mt-2 text-xs text-text-brand">Ramificações: {step.branch}</p>
                      ) : null}
                      <p className="mt-2 text-xs text-text-muted">
                        Responsável: {step.responsible ?? 'Não definido'} · Sistemas:{' '}
                        {step.systems.join(', ') || 'Não definido'} · Resultado:{' '}
                        {step.expectedOutcome ?? 'Não definido'}
                      </p>
                      <div className="mt-2 flex gap-3 text-xs">
                        <button
                          className="underline"
                          onClick={() => {
                            setEditingStep(step);
                            setStepDraft({
                              title: step.title,
                              description: step.description,
                              outcome: step.expectedOutcome ?? '',
                            });
                          }}
                        >
                          Editar
                        </button>
                        <button
                          disabled={index === 0}
                          className="underline disabled:opacity-30"
                          onClick={() => moveStep(step.nodeRefs[0]!, -1)}
                        >
                          Mover para cima
                        </button>
                        <button
                          disabled={index === document.sop.content.steps.length - 1}
                          className="underline disabled:opacity-30"
                          onClick={() => moveStep(step.nodeRefs[0]!, 1)}
                        >
                          Mover para baixo
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </Section>
          <Section
            id="checklist"
            title="Checklist"
            source={document.checklist.source}
            actions={
              <>
                <button className="text-xs underline" onClick={addChecklistItem}>
                  Adicionar item
                </button>
                <button className="text-xs underline" onClick={persistChecklist}>
                  Salvar projeção atual
                </button>
                {config.checklistItems?.length ? (
                  <button
                    className="text-xs underline"
                    onClick={() =>
                      updateConfig((current) => {
                        const next = { ...current };
                        delete next.checklistItems;
                        return next;
                      })
                    }
                  >
                  Redefinir para POP
                  </button>
                ) : null}
              </>
            }
          >
            <p className="mb-4 text-xs text-text-muted">
              As marcações valem apenas nesta sessão e nunca são salvas como histórico de tarefas.
            </p>
            <ul className="space-y-2">
              {document.checklist.content.map((item, index) => (
                <li key={item.id}>
                  <label className="flex items-center gap-3 rounded border border-border-subtle p-3">
                    <input
                      type="checkbox"
                      checked={checked.has(item.id)}
                      onChange={(event) =>
                        setChecked((current) => {
                          const next = new Set(current);
                          if (event.target.checked) next.add(item.id);
                          else next.delete(item.id);
                          return next;
                        })
                      }
                    />
                    <span className="flex-1">{item.label}</span>
                    <span className="text-xs text-text-muted">
                      {item.required ? 'Obrigatório' : 'Opcional'}
                    </span>
                    {item.nodeRefs[0] ? (
                      <button
                        type="button"
                        className="text-xs underline"
                        onClick={() => focusNode(item.nodeRefs[0]!)}
                      >
                        Abrir etapa
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="text-xs underline"
                      onClick={() => {
                        setEditingChecklist(item);
                        setChecklistDraft({ label: item.label, required: item.required });
                      }}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      disabled={index === 0}
                      className="text-xs underline disabled:opacity-30"
                      onClick={() => moveChecklistItem(item.id, -1)}
                    >
                      Acima
                    </button>
                    <button
                      type="button"
                      disabled={index === document.checklist.content.length - 1}
                      className="text-xs underline disabled:opacity-30"
                      onClick={() => moveChecklistItem(item.id, 1)}
                    >
                      Abaixo
                    </button>
                  </label>
                </li>
              ))}
            </ul>
          </Section>
          {document.attentionPoints ? (
            <Section id="attention-points" title="Pontos de atenção" source="derived">
              <div className="space-y-3">
                {document.attentionPoints.content.map((item) => (
                  <article key={item.title} className="rounded border border-border-subtle p-3">
                    <h3 className="font-medium">{item.title}</h3>
                    <p className="text-xs uppercase text-text-muted">{localizeSystemValue(item.severity)}</p>
                    <ul className="mt-2 list-disc pl-5 text-sm text-text-secondary">
                      {item.evidence.map((evidence) => (
                        <li key={evidence}>{evidence}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </Section>
          ) : null}
        </article>
      </div>
      <Dialog
        open={editingStep !== null}
        onClose={() => setEditingStep(null)}
        title="Editar etapa do POP"
      >
        <label className="block text-sm">
          Título
          <input
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={stepDraft.title}
            onChange={(event) => setStepDraft({ ...stepDraft, title: event.target.value })}
          />
        </label>
        <label className="mt-3 block text-sm">
          Descrição
          <textarea
            rows={4}
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={stepDraft.description}
            onChange={(event) => setStepDraft({ ...stepDraft, description: event.target.value })}
          />
        </label>
        <label className="mt-3 block text-sm">
          Resultado esperado
          <input
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={stepDraft.outcome}
            onChange={(event) => setStepDraft({ ...stepDraft, outcome: event.target.value })}
          />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setEditingStep(null)}>
            Cancelar
          </Button>
          <Button onClick={saveStep}>Salvar etapa manual</Button>
        </div>
      </Dialog>
      <Dialog open={editingKpi !== null} onClose={() => setEditingKpi(null)} title="Configurar KPI">
        <label className="block text-sm">
          Rótulo de exibição
          <input
            aria-label="Rótulo de exibição do KPI"
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={kpiDraft.label}
            onChange={(event) => setKpiDraft({ ...kpiDraft, label: event.target.value })}
          />
        </label>
        <label className="mt-3 block text-sm">
          Responsável
          <input
            aria-label="Responsável pelo KPI"
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={kpiDraft.owner}
            onChange={(event) => setKpiDraft({ ...kpiDraft, owner: event.target.value })}
          />
        </label>
        <label className="mt-3 block text-sm">
          Meta
          <input
            aria-label="Meta do KPI"
            type="number"
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={kpiDraft.target}
            onChange={(event) => setKpiDraft({ ...kpiDraft, target: event.target.value })}
          />
        </label>
        <label className="mt-3 block text-sm">
          Período da meta
          <input
            aria-label="Período da meta do KPI"
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={kpiDraft.period}
            onChange={(event) => setKpiDraft({ ...kpiDraft, period: event.target.value })}
          />
        </label>
        <label className="mt-3 block text-sm">
          Observação
          <textarea
            aria-label="Observação do KPI"
            rows={3}
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={kpiDraft.note}
            onChange={(event) => setKpiDraft({ ...kpiDraft, note: event.target.value })}
          />
        </label>
        <p className="mt-2 text-xs text-text-muted">
          O valor atual permanece somente leitura e sempre vem do motor de métricas.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setEditingKpi(null)}>
            Cancelar
          </Button>
          <Button onClick={saveKpi}>Salvar exibição do KPI</Button>
        </div>
      </Dialog>
      <Dialog
        open={editingChecklist !== null}
        onClose={() => setEditingChecklist(null)}
        title="Editar item da checklist"
      >
        <label className="block text-sm">
          Rótulo
          <input
            aria-label="Rótulo do item da checklist"
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={checklistDraft.label}
            onChange={(event) =>
              setChecklistDraft({ ...checklistDraft, label: event.target.value })
            }
          />
        </label>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={checklistDraft.required}
            onChange={(event) =>
              setChecklistDraft({ ...checklistDraft, required: event.target.checked })
            }
          />
          Item obrigatório do procedimento
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setEditingChecklist(null)}>
            Cancelar
          </Button>
          <Button onClick={saveChecklistItem}>Salvar item da checklist</Button>
        </div>
      </Dialog>
      <Dialog
        open={exportSettingsOpen}
        onClose={() => setExportSettingsOpen(false)}
        title="Configurações de exportação da documentação"
      >
        <label className="block text-sm">
          Nome do cliente
          <input
            aria-label="Nome do cliente na exportação"
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={exportDraft.clientName}
            onChange={(event) => setExportDraft({ ...exportDraft, clientName: event.target.value })}
          />
        </label>
        <label className="mt-3 block text-sm">
          Marca
          <select
            aria-label="Marca da exportação"
            className="mt-1 w-full rounded border border-border-subtle bg-surface p-2"
            value={exportDraft.branding}
            onChange={(event) =>
              setExportDraft({
                ...exportDraft,
                branding: event.target.value as typeof exportDraft.branding,
              })
            }
          >
            <option value="dark_funnels">Dark Funnels</option>
            <option value="client">Cliente</option>
            <option value="minimal">Mínima</option>
          </select>
        </label>
        <p className="mt-2 text-xs text-text-muted">
          A ausência de logotipo nunca bloqueia a exportação; a marca em texto é usada como alternativa.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setExportSettingsOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              updateConfig((current) => ({
                ...current,
                export: {
                  branding: exportDraft.branding,
                  ...(exportDraft.clientName.trim()
                    ? { clientName: exportDraft.clientName.trim() }
                    : {}),
                },
              }));
              setExportSettingsOpen(false);
            }}
          >
            Salvar configurações de exportação
          </Button>
        </div>
      </Dialog>
      <Dialog open={regenerateOpen} onClose={() => setRegenerateOpen(false)} title="Gerar POP novamente">
        <p className="text-sm text-text-secondary">
          A versão derivada do grafo está pronta. A redação e a ordem manuais permanecerão, a menos que você
          as redefina explicitamente.
        </p>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={() => setRegenerateOpen(false)}>
            Manter edições manuais
          </Button>
          <Button variant="danger" onClick={resetSop}>
            Substituir pela derivada
          </Button>
        </div>
      </Dialog>
      <Dialog
        open={aiPreview !== null}
        onClose={() => setAiPreview(null)}
        title="Revisar visão geral assistida por IA"
      >
        <p className="mb-2 text-xs text-text-muted">
          O resultado da IA é uma proposta. Confirme para substituir a visão geral personalizada.
        </p>
        <textarea
          aria-label="Prévia da visão geral da IA"
          rows={7}
          className="w-full rounded border border-border-subtle bg-surface p-3"
          value={aiPreview ?? ''}
          onChange={(event) => setAiPreview(event.target.value)}
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setAiPreview(null)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (aiPreview) saveOverview(aiPreview, 'ai_assisted');
              setAiPreview(null);
            }}
          >
            Confirmar texto assistido por IA
          </Button>
        </div>
      </Dialog>
      <div ref={exportRef}>
        <DocumentationExportSurface
          name={name}
          document={document as GeneratedDocumentation}
          clientName={config.export.clientName}
          branding={config.export.branding}
        />
      </div>
    </main>
  );
}
